/* @flow */

import * as React from 'react';
import glamorous from 'glamorous';
import { padding } from 'polished';
import { isNumber } from 'lodash/fp';
import t from 'app/utils/translate';
import MultiSwitch from 'app/components/commons/MultiSwitch';
import RadioButton from 'app/components/commons/RadioButton';
import Responsive from 'app/components/responsive/Responsive';
import Tappable from 'app/components/commons/Tappable';
import { PRIZE, STATUS } from 'app/constants/leaderBoardColumnTypes';

type Props = {
  displayPlayersCount: boolean,
  dynamicColumn: string,
  hasProjections: boolean,
  onColumnSelect: string => void,
  onProjectedChange: () => void,
  ptsToCash?: number,
  isRunning: boolean,
  showProjected: boolean
};

const Wrapper = glamorous.div(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.color.basis6,
  color: theme.color.basis1,
  display: 'flex',
  fontSize: theme.fontSize.xxsmall,
  fontWeight: theme.fontWeight.demiBold,
  ...padding(theme.spacing.xsmall, 0),
  textTransform: 'uppercase'
}));

const Cell = glamorous.div(({ theme }) => ({
  display: 'flex',
  ...padding(null, theme.spacing.xsmall),
  textTransform: 'uppercase',
  [theme.mediaQuery.desktopAndUp]: padding(null, theme.spacing.default)
}));

const CellMain = glamorous(Cell)({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  alignItems: 'center'
});

const CellDefault = glamorous(Cell)(({ theme }) => ({
  justifyContent: 'center',
  width: 70,
  [theme.mediaQuery.desktopAndUp]: {
    width: 100
  }
}));

const Clickable = glamorous.div({
  alignItems: 'center',
  display: 'flex'
});

function LeaderBoardHead({
  displayPlayersCount,
  dynamicColumn,
  hasProjections,
  isRunning,
  onColumnSelect,
  onProjectedChange,
  ptsToCash,
  showProjected
}: Props) {
  return (
    <Responsive tabletLandscape desktop>
      {isLargeScreen => (
        <Wrapper>
          {hasProjections && (
            <CellMain>
              <Tappable component={Clickable} onTap={onProjectedChange}>
                <RadioButton checked={showProjected} />
                {'\u00a0'}
                Projected Score{'\u00a0'}
              </Tappable>
            </CellMain>
          )}
          <CellMain>
            {isNumber(ptsToCash) &&
              `${isRunning ? 'CURRENT ' : ''}PTS TO CASH: ${ptsToCash.toFixed(2)}`}
          </CellMain>
          {(isLargeScreen || !displayPlayersCount) && <CellDefault>{t('Prize')}</CellDefault>}
          {isLargeScreen && displayPlayersCount && <CellDefault>{t('Status')}</CellDefault>}
          {!isLargeScreen && displayPlayersCount && (
            <MultiSwitch
              onSelect={onColumnSelect}
              options={[
                {
                  label: t('Prize'),
                  value: PRIZE
                },
                {
                  label: t('Status'),
                  value: STATUS
                }
              ]}
              value={dynamicColumn}
            />
          )}
          <CellDefault>{t('Points')}</CellDefault>
        </Wrapper>
      )}
    </Responsive>
  );
}

export default LeaderBoardHead;
